import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

import { Message, Spin, Typography } from '@arco-design/web-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const t = i18n.t.bind(i18n);
const { Text } = Typography;

const QRCodeContainer = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  display: block;
  position: relative;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 242px;
  min-height: 242px;

  canvas {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const StatusOverlay = styled.div<{ $color: string }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.8);
  color: ${(props) => props.$color};
`;

const Tips = styled(Text)`
  display: block;
  margin-top: 12px;
  color: var(--color-text-3);
  font-size: 12px;
  text-align: center;
`;

// 充值状态枚举
export const RECHARGE_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;

export const statusConfig = {
  [RECHARGE_STATUS.PENDING]: { text: t('common:pending.recharge'), color: 'gray' },
  [RECHARGE_STATUS.APPROVED]: { text: t('common:recharge.successful'), color: 'green' },
  [RECHARGE_STATUS.REJECTED]: { text: t('common:recharge.failed'), color: 'red' },
  [RECHARGE_STATUS.CANCELLED]: { text: t('common:recharge.canceled'), color: 'gray' },
};

export type RechargeStatus = keyof typeof RECHARGE_STATUS;

interface WechatPayQRCodeProps {
  payUrl: string;
  payStatus?: RechargeStatus;
  onGenerate?: (url: string) => void;
  loading?: boolean;
  timeout?: number;
  style?: React.CSSProperties;
}

// 传入url，生成微信充值二维码
const WechatPayQRCode: React.FC<WechatPayQRCodeProps> = ({
  payUrl = '',
  payStatus = 'PENDING',
  onGenerate,
  loading,
  timeout,
  style = {},
}) => {
  const { t } = useTranslation();
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeInstance = useRef<any>(null);
  const [internalLoading, setInternalLoading] = useState<boolean>(true);

  const isLoading = loading || internalLoading;

  // 动态加载 QRCode 库
  const loadQRCodeLibrary = (): Promise<typeof QRCode> => {
    return new Promise((resolve, reject) => {
      if (typeof QRCode !== 'undefined') {
        resolve(QRCode);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.bootcdn.net/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
      script.onload = () => {
        if (typeof QRCode !== 'undefined') {
          resolve(QRCode);
        } else {
          reject(new Error('QRCode library failed to load'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load QRCode library'));
      document.head.appendChild(script);
    });
  };

  const generateQRCode = async () => {
    const url = payUrl.trim();

    if (!url) {
      Message.error(t('components:recharge.link.cannot.be.empty'));
      return;
    }

    // 验证链接格式
    if (!url.startsWith('weixin://') && !url.startsWith('http://') && !url.startsWith('https://')) {
      Message.error(t('components:please.enter.a.valid.wechat.recharge.link'));
      return;
    }

    try {
      setInternalLoading(true);

      // 清空之前的二维码
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
      }

      // 如果已经有二维码实例，先清除
      if (qrCodeInstance.current) {
        qrCodeInstance.current = null;
      }

      const QRCode = await loadQRCodeLibrary();

      // 生成新二维码
      qrCodeInstance.current = new QRCode(qrCodeRef.current, {
        text: url,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M,
      });

      onGenerate?.(url);
    } catch (error) {
      Message.error(t('components:failed.to.generate.qr.code'));
      console.error('QR Code generation failed:', error);
      generateQRCode();
    } finally {
      setInternalLoading(false);
    }
  };

  useEffect(() => {
    if (payUrl) {
      generateQRCode();
    }
  }, [payUrl]);

  // 清理函数
  useEffect(() => {
    return () => {
      // 组件卸载时清理二维码实例
      if (qrCodeInstance.current && qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
        qrCodeInstance.current = null;
      }
    };
  }, []);

  const { text, color } = useMemo(() => {
    return statusConfig[payStatus] || statusConfig[RECHARGE_STATUS.PENDING];
  }, [payStatus]);

  const showOverlay = payStatus !== RECHARGE_STATUS.PENDING && !isLoading;

  return (
    <Spin loading={isLoading} style={style}>
      <h4>{t('common:wechat.recharge')}</h4>
      <Tips>
        {timeout
          ? t('common:qrcode.expire.minutes', { minutes: timeout })
          : t(
              'components:after.successful.recharge.the.system.will.automatically.confirm.the.deposit',
            )}
      </Tips>
      <QRCodeContainer>
        <div ref={qrCodeRef} />
        {showOverlay && <StatusOverlay $color={color}>{text}</StatusOverlay>}
      </QRCodeContainer>
      <Tips>{t('components:scan.the.qr.code.with.wechat.to.complete.the.recharge')}</Tips>
    </Spin>
  );
};

export default WechatPayQRCode;
